import type { Podcast, PodcastLine } from '../types/podcast'
import { sanitizeFilename } from './file-download'

interface ExportOptions {
  podcast: Podcast
  hostAName?: string
  hostBName?: string
}

/**
 * Generates a styled HTML document for the podcast script
 * and triggers a print dialog for PDF export.
 */
export const exportScriptToPdf = ({ podcast, hostAName, hostBName }: ExportOptions): void => {
  const lines = [...podcast.podcastLines].sort((a, b) => a.sequenceOrder - b.sequenceOrder)
  const title = podcast.title || 'Untitled Podcast'
  const safeTitle = sanitizeFilename(title)
  
  // Get display names for hosts
  const hostA = hostAName || 'Host A'
  const hostB = hostBName || 'Host B'

  const htmlContent = generatePdfHtml(title, lines, hostA, hostB, podcast)
  
  // Open a new window for printing
  const printWindow = window.open('', '_blank', 'width=800,height=600')
  if (!printWindow) {
    alert('Please allow pop-ups to export the script as PDF.')
    return
  }

  printWindow.document.write(htmlContent)
  printWindow.document.close()
  
  // Set the document title for the PDF filename
  printWindow.document.title = `${safeTitle} - Script`
  
  // Wait for content to load, then trigger print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print()
    }, 250)
  }
}

function generatePdfHtml(
  title: string,
  lines: PodcastLine[],
  hostA: string,
  hostB: string,
  podcast: Podcast
): string {
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  const estimatedDuration = podcast.durationSeconds
    ? formatDuration(podcast.durationSeconds)
    : podcast.durationMinutes
    ? `~${podcast.durationMinutes} min`
    : null

  const dialogueHtml = lines.map((line) => {
    const speaker = line.host === 'A' ? hostA : hostB
    const isHostA = line.host === 'A'
    
    return `
      <div class="line ${isHostA ? 'host-a' : 'host-b'}">
        <div class="speaker-badge ${isHostA ? 'badge-a' : 'badge-b'}">${escapeHtml(speaker)}</div>
        <div class="dialogue">${escapeHtml(line.text)}</div>
      </div>
    `
  }).join('')

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)} - Script</title>
  <style>
    @page {
      margin: 0.75in 0.75in 1in 0.75in;
      size: letter;
    }
    
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #1a1a1a;
      background: #fff;
      padding: 0;
    }
    
    .container {
      max-width: 7in;
      margin: 0 auto;
      padding: 0.5in;
      padding-bottom: 1in;
    }
    
    @media print {
      .container {
        padding: 0;
        padding-bottom: 0.5in;
        max-width: none;
      }
    }
    
    /* Header */
    .header {
      margin-bottom: 2rem;
      padding-bottom: 1.5rem;
      border-bottom: 2px solid #e5e5e5;
    }
    
    .title {
      font-size: 24pt;
      font-weight: 700;
      color: #111;
      margin-bottom: 0.5rem;
      line-height: 1.2;
    }
    
    .meta {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;
      margin-top: 1rem;
      font-size: 10pt;
      color: #666;
    }
    
    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }
    
    .meta-label {
      font-weight: 600;
      color: #444;
    }
    
    /* Host Legend */
    .hosts-legend {
      display: flex;
      gap: 1.5rem;
      margin-bottom: 2rem;
      padding: 1rem 1.25rem;
      background: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e5e5e5;
    }
    
    .host-legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 10pt;
    }
    
    .host-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }
    
    .dot-a {
      background: #059669;
    }
    
    .dot-b {
      background: #3b82f6;
    }
    
    /* Script Section */
    .script-section {
      page-break-inside: auto;
    }
    
    .section-title {
      font-size: 12pt;
      font-weight: 600;
      color: #333;
      margin-bottom: 1rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    
    /* Dialogue Lines */
    .line {
      margin-bottom: 1.25rem;
      page-break-inside: avoid;
    }
    
    .speaker-badge {
      display: inline-block;
      font-size: 9pt;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      padding: 0.2rem 0.6rem;
      border-radius: 4px;
      margin-bottom: 0.35rem;
    }
    
    .badge-a {
      background: #d1fae5;
      color: #065f46;
    }
    
    .badge-b {
      background: #dbeafe;
      color: #1e40af;
    }
    
    .dialogue {
      padding-left: 0.25rem;
      font-size: 11pt;
      line-height: 1.7;
      color: #1f2937;
    }
    
    /* Footer */
    .footer {
      margin-top: 3rem;
      padding-top: 1rem;
      border-top: 1px solid #e5e5e5;
      font-size: 9pt;
      color: #999;
      text-align: center;
    }
    
    .footer a {
      color: #666;
      text-decoration: none;
    }
    
    @media print {
      .footer {
        margin-top: 2rem;
        page-break-inside: avoid;
      }
    }
    
    /* Screen-only styles for the print preview */
    @media screen {
      body {
        background: #f5f5f5;
        padding: 2rem;
      }
      
      .container {
        background: white;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        border-radius: 8px;
        padding: 2rem;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <header class="header">
      <h1 class="title">${escapeHtml(title)}</h1>
      <div class="meta">
        <div class="meta-item">
          <span class="meta-label">Created:</span>
          <span>${formatDate(podcast.createdAt)}</span>
        </div>
        ${estimatedDuration ? `
        <div class="meta-item">
          <span class="meta-label">Duration:</span>
          <span>${estimatedDuration}</span>
        </div>
        ` : ''}
        <div class="meta-item">
          <span class="meta-label">Lines:</span>
          <span>${lines.length}</span>
        </div>
      </div>
    </header>
    
    <div class="hosts-legend">
      <div class="host-legend-item">
        <div class="host-dot dot-a"></div>
        <span><strong>${escapeHtml(hostA)}</strong></span>
      </div>
      <div class="host-legend-item">
        <div class="host-dot dot-b"></div>
        <span><strong>${escapeHtml(hostB)}</strong></span>
      </div>
    </div>
    
    <section class="script-section">
      <h2 class="section-title">Script</h2>
      ${dialogueHtml}
    </section>
    
    <footer class="footer">
      Generated with <a href="https://podcast-generator.ai">podcast-generator.ai</a>
    </footer>
  </div>
</body>
</html>
  `.trim()
}

function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60)
    const remainingMins = mins % 60
    return `${hrs}h ${remainingMins}m`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
